import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import vehicleBg from "../assets/vehicle-bg.png";
import Loading from "../../../components/CommonLoading";
import MessageBox from "../../../components/message-box";
import { uploadFileForId } from "../../../components/AttachmentUpload/service";
import useNoiseInfo from "../store/useNoiseInfo";
import styles from "./index.module.scss";

interface NoiseInfoImageProps {
  visible: boolean; // 是否显示弹窗
  onClose: () => void; // 关闭弹窗（标注确定后）
  /** 查看态：仅展示已标注红点，不可点选/标注，底部为「关闭」（用于详情只读页查看异响位置） */
  readOnly?: boolean;
}

/**
 * 异响位置图（选择异响位置）——弹窗形式（由异响反馈编辑态 NoiseFeed/Edit 内联渲染，点「去标记/修改标记」直接打开）。
 * - 点击图片任意位置标注红点（双圆），点别处移动；点「确定」合成含点 JPEG 上传得 fileId 存 store → onClose。
 * - 未标注时「确定」不可点。
 * - readOnly 查看态：不可点选，仅按 store 里的 xPct/yPct 展示红点，底部按钮为「关闭」。
 */
export default function NoiseInfoImage(props: NoiseInfoImageProps) {
  const { visible, onClose, readOnly = false } = props;
  const imgRef = useRef<HTMLImageElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);
  const { noiseInfo, setNoisePosition, setNoisePositionImg } = useNoiseInfo();
  const { xPct, yPct } = noiseInfo;
  const hasPoint = xPct != null && yPct != null;
  const [uploading, setUploading] = useState(false);
  // 同步防抖守卫：uploading 是 React state，重渲染前极快的二次点击会读到旧值 false 而重复上传；
  // ref 立即置位，杜绝并发上传。UI 禁用态仍用 uploading state 驱动。
  const uploadingRef = useRef(false);

  // 预加载底图：组件常驻挂载（NoiseFeed 渲染即挂载），进入异响子项时就预热浏览器缓存，
  // 避免首次点「去标记」打开弹窗时 <img> 才开始从 CDN 下载导致的白屏（第二次因缓存已秒显）。
  useEffect(() => {
    const img = new Image();
    img.src = vehicleBg;
  }, []);

  // 点击图片：记录相对图片的百分比坐标（0~1），点别处即移动
  const handlePick = (e: React.MouseEvent<HTMLDivElement>) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    const x = clamp((e.clientX - rect.left) / rect.width);
    const y = clamp((e.clientY - rect.top) / rect.height);
    setNoisePosition(x, y);
  };

  // 以 crossOrigin 方式加载一张同源/CORS 干净的底图，专供 canvas 绘制使用。
  // 生产环境 publicPath 指向 CDN（见 config-overrides.js 的 setWebpackPublicPath），
  // 直接把跨域 <img> 画进 canvas 会污染画布，导致 toBlob/toDataURL 抛 SecurityError；
  // 用带 crossOrigin="anonymous" 的图片加载（CDN 需返回 Access-Control-Allow-Origin）即可避免污染。
  // 加载失败或超时（如 CDN 未配 CORS、弱网）则回退到展示用的 <img>，避免流程卡死。
  const loadDrawableImage = (fallback: HTMLImageElement): Promise<HTMLImageElement> =>
    new Promise((resolve) => {
      const src = fallback.currentSrc || fallback.src;
      console.log("[NoiseInfoImage] loadDrawableImage src=", src, "pageOrigin=", window.location.origin);
      if (!src) {
        console.warn("[NoiseInfoImage] 无 src，回退展示用 img");
        resolve(fallback);
        return;
      }
      let settled = false;
      const done = (img: HTMLImageElement) => {
        if (settled) return;
        settled = true;
        resolve(img);
      };
      const corsImg = new Image();
      corsImg.crossOrigin = "anonymous";
      corsImg.onload = () => {
        console.log("[NoiseInfoImage] CORS 图片加载成功，画布不会被污染", corsImg.naturalWidth, "x", corsImg.naturalHeight);
        done(corsImg);
      };
      corsImg.onerror = (e) => {
        console.warn("[NoiseInfoImage] CORS 图片加载失败(CDN 可能未配 CORS)，回退展示用 img", e);
        done(fallback);
      };
      corsImg.src = src;
      // 超时兜底：某些 webview 下 onload/onerror 都可能不触发，避免 Promise 永久挂起
      setTimeout(() => {
        if (!settled) console.warn("[NoiseInfoImage] CORS 图片加载超时(8s)，回退展示用 img");
        done(fallback);
      }, 8000);
    });

  // 合成「底图 + 红点」为 JPEG File（按图片自然分辨率，保证清晰）
  const buildCompositeFile = async (): Promise<File | null> => {
    const img = imgRef.current;
    const marker = markerRef.current;
    console.log("[NoiseInfoImage] buildCompositeFile 开始", {
      hasImg: !!img,
      hasMarker: !!marker,
      xPct,
      yPct,
    });
    if (!img || !marker || xPct == null || yPct == null) {
      console.warn("[NoiseInfoImage] 前置条件不满足，返回 null");
      return null;
    }
    const displayedW = img.getBoundingClientRect().width;

    // 用 CORS 干净的底图绘制，避免跨域画布污染
    const drawImg = await loadDrawableImage(img);
    const naturalW = drawImg.naturalWidth || img.naturalWidth;
    const naturalH = drawImg.naturalHeight || img.naturalHeight;
    console.log("[NoiseInfoImage] 尺寸", { naturalW, naturalH, displayedW });
    if (!naturalW || !displayedW) {
      console.warn("[NoiseInfoImage] 尺寸为 0，返回 null");
      return null;
    }

    const scale = naturalW / displayedW; // 显示态 → 自然分辨率
    const cx = xPct * naturalW;
    const cy = yPct * naturalH;
    const outerR = (marker.offsetWidth * scale) / 2; // 外圈半径
    const innerR = outerR / 2; // 内圈直径为外圈一半（96/48）

    const canvas = document.createElement("canvas");
    canvas.width = naturalW;
    canvas.height = naturalH;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.warn("[NoiseInfoImage] getContext('2d') 返回 null，返回 null");
      return null;
    }

    ctx.drawImage(drawImg, 0, 0, naturalW, naturalH);

    // 外圈：radial-gradient(#ff4d4f1a 0% → #ff4d4f99 100%)
    const gOuter = ctx.createRadialGradient(cx, cy, 0, cx, cy, outerR);
    gOuter.addColorStop(0, "rgba(255,77,79,0.1)");
    gOuter.addColorStop(1, "rgba(255,77,79,0.6)");
    ctx.fillStyle = gOuter;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.fill();

    // 内圈：radial-gradient(#ff4d4f33 0% → #ff4d4f 100%)
    const gInner = ctx.createRadialGradient(cx, cy, 0, cx, cy, innerR);
    gInner.addColorStop(0, "rgba(255,77,79,0.2)");
    gInner.addColorStop(1, "rgba(255,77,79,1)");
    ctx.fillStyle = gInner;
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fill();

    return new Promise((resolve) => {
      const toFile = (blob: Blob | null) => {
        console.log("[NoiseInfoImage] toBlob 回调，blob size=", blob?.size ?? "null");
        resolve(
          blob
            ? new File([blob], "noise-position.jpg", { type: "image/jpeg" })
            : null
        );
      };
      try {
        if (typeof canvas.toBlob === "function") {
          console.log("[NoiseInfoImage] 走 canvas.toBlob 导出");
          canvas.toBlob(toFile, "image/jpeg", 0.7);
        } else {
          // 老 webview 无 toBlob：dataURL → Blob 兜底
          console.log("[NoiseInfoImage] 无 toBlob，走 toDataURL 兜底");
          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
          const b64 = dataUrl.split(",")[1] || "";
          const bin = atob(b64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          toFile(new Blob([arr], { type: "image/jpeg" }));
        }
      } catch (e) {
        // 画布被跨域图片污染时，toBlob/toDataURL 会抛 SecurityError，返回 null 走「生成图片失败」提示
        const err = e as any;
        console.error("[NoiseInfoImage] canvas 导出异常(疑似画布污染):", err?.name, err?.message, err);
        resolve(null);
      }
    });
  };

  const handleConfirm = async () => {
    if (uploadingRef.current || !hasPoint) return; // 上传中或未标注不可确定（ref 同步防抖）
    console.log("[NoiseInfoImage] handleConfirm 点击，positionImg=", noiseInfo.positionImg);
    // 尚未上传过当前点（移动会清空 positionImg）：合成含点图并上传
    if (!noiseInfo.positionImg) {
      try {
        uploadingRef.current = true;
        setUploading(true);
        Loading.show("", 2000); // 第二参为 zIndex，高于弹窗(z-index:1000)否则被盖住不可见
        const file = await buildCompositeFile();
        console.log("[NoiseInfoImage] buildCompositeFile 结果 file=", file, "size=", file?.size);
        if (!file) {
          console.warn("[NoiseInfoImage] file 为空，提示生成图片失败");
          MessageBox.toast("生成图片失败，请重试");
          return;
        }
        console.log("[NoiseInfoImage] 开始调用 uploadFileForId → serviceOrder/get/upload/url");
        const fileId = await uploadFileForId(file);
        console.log("[NoiseInfoImage] 上传成功 fileId=", fileId);
        setNoisePositionImg(fileId);
      } catch (err) {
        const e = err as any;
        console.error("[NoiseInfoImage] handleConfirm 异常:", e?.name, e?.message, e);
        MessageBox.toast("图片上传失败，请重试");
        return;
      } finally {
        Loading.hide();
        uploadingRef.current = false;
        setUploading(false);
      }
    }
    onClose();
  };

  if (!visible) return null;

  return (
    <div className={styles.mask}>
      <div className={styles.dialog}>
        <div className={styles.imageCard}>
          <div
            className={styles.imageWrap}
            onClick={readOnly ? undefined : handlePick}
          >
            <img
              ref={imgRef}
              className={styles.image}
              src={vehicleBg}
              alt="异响位置"
            />
            {hasPoint && (
              <div
                ref={markerRef}
                className={styles.marker}
                style={{ left: `${xPct! * 100}%`, top: `${yPct! * 100}%` }}
              >
                <span className={styles.markerInner} />
              </div>
            )}
          </div>
        </div>

        <div className={styles.bottomBar}>
          {readOnly ? (
            <div className={styles.confirmBtn} onClick={onClose}>
              关闭
            </div>
          ) : (
            <div
              className={classNames(styles.confirmBtn, {
                [styles.btnDisabled]: uploading || !hasPoint,
              })}
              onClick={handleConfirm}
            >
              确定
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
